import { Button, Flex, IconButton, Input, InputGroup, InputLeftElement, TableContainer, Tooltip } from "@chakra-ui/react";
import { IconEdit, IconLink } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuSearch } from "react-icons/lu";
import { TransformedClientData as Client, TableRowProps } from "../../../utils/types";
import { CreateOrEditClientModal } from "./CreateOrEditClientModal";
import { SectionHeader } from "../../../components/SectionHeader";
import { useGetClients } from "../../../api/clients/clientQueries";

export const ClientPanel: FunctionComponent = () => {
    const [showCreateOrEditClientDialog, setShowCreateOrEditClientDialog] = useState(false);
    const [clientData, setClientData] = useState<Client>();
    const navigate = useNavigate();
    const { data: apiClients = [], isLoading, refetch: refetchClients } = useGetClients();
    const [searchTerm, setSearchTerm] = useState<string>("");

    const columnHelper = createColumnHelper<Client>();

    const clients = apiClients.map(c => ({
        id: c.id,
        name: c.name,
        agressoCode: c.agressoCode,
        leadMD: c.leadMD,
        industryAnalysis: c.industryAnalysisBlobPath,
        orgDiagram: c.orgDiagramBlobPath,
        abbreviation: c.abbreviation,
        headcount: c.headcount,
        description: c.description,
        endingDate: c.endingDate,
        industry: c.industry,
        overviewOfIndustry: c.overviewOfIndustry,
        productsAndServices: c.productsAndServices,
        majorPlayers: c.majorPlayers,
        industryPerformance: c.industryPerformance,
        competitiveLandscape: c.competitiveLandscape,
        keyExternalDrivers: c.keyExternalDrivers,
        barriersToEntry: c.barriersToEntry,
    }));

    const filteredData = useMemo(() => {
        if (searchTerm.trim() === "") {
            return clients ?? [];
        } else {
            const value = searchTerm.trim().toLowerCase();
            return clients.filter(
                (c: Client) =>
                    c.name?.toLowerCase().includes(value) ||
                    c.agressoCode?.toLowerCase().includes(value) ||
                    c.leadMD?.toLowerCase().includes(value) ||
                    c.industryAnalysis?.toLowerCase().includes(value) ||
                    c.orgDiagram?.toLowerCase().includes(value) ||
                    c.abbreviation?.toLowerCase().includes(value),
            );
        }
    }, [clients, searchTerm]);

    function enableEditClientModal(client: Client) {
        setClientData(client);
        setShowCreateOrEditClientDialog(true);
    }

    function closeCreateOrEditClientDialog() {
        setClientData(undefined);
        setShowCreateOrEditClientDialog(false);
        refetchClients();
    }

    function handleClientClick(client: Client) {
        navigate(`/clients/${client.id}`);
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        setSearchTerm(event.target.value);
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Clients">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input placeholder="Search Clients" onChange={handleFilter} />
                </InputGroup>
                <Button leftIcon={<IconLink />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditClientDialog(true)}>
                    Link Engage Client
                </Button>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                {" "}
                <DataTable
                    enableFilters={false}
                    tableProps={{
                        size: "sm",
                        sx: {
                            "tbody tr": {
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                _hover: {
                                    backgroundColor: "blue.50",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    _dark: {
                                        backgroundColor: "blue.900", // Adjust hover color for dark mode
                                    },
                                },
                            },
                        },
                    }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No clients found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    onRowClicked={client => handleClientClick(client)}
                    columns={[
                        columnHelper.accessor("name", {
                            header: "Name",
                            cell: (props: TableRowProps<Client>) => props.getValue(),
                        }),
                        columnHelper.accessor("agressoCode", {
                            header: "Aggresso Code",
                            cell: (props: TableRowProps<Client>) => props.getValue(),
                        }),
                        columnHelper.accessor("leadMD", {
                            header: "Lead MD",
                            cell: (props: TableRowProps<Client>) => props.getValue(),
                        }),
                        columnHelper.accessor("abbreviation", {
                            header: "Abbreviation",
                            cell: (props: TableRowProps<Client>) => props.getValue(),
                        }),
                        {
                            id: "Edit Client",
                            cell: (props: TableRowProps<Client>) => {
                                const client = props.row.original;
                                return (
                                    <Tooltip label="Edit Client">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit Client"
                                            variant="outline"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEditClientModal(client);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <Flex>
                <CreateOrEditClientModal
                    show={showCreateOrEditClientDialog}
                    onClose={closeCreateOrEditClientDialog}
                    clientData={clientData}
                    clientNames={clients ?? []}
                    isEdit={!!clientData}
                />
            </Flex>
        </Flex>
    );
};
